/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useEffect, useMemo } from 'react';
import { Route, Routes, useLocation } from 'react-router';
import { AppRoutes } from '@/constants/routes';
import { useBreadCrumbs, useProjectBreadCrumbLink } from '@/hooks/useBreadCrumbs';
import { AnchorDiv, CBreadCrumbs } from '@/components';
import { useProjectPath } from '@/hooks/useProjectPath';
import { ProjectListPage } from './ProjectsPage/ProjectListPage';
import { ProjectDetailPage } from './ProjectsPage/ProjectDetailPage';
import { RedirectHome } from '@/components/RedirectHome';

export const ProjectsPage: React.FC = () => {
  const { homeLink, projectListLink } = useBreadCrumbs();
  const { pathname } = useLocation();
  const { toBrowserProjectPath } = useProjectPath();
  const { projectLink, setMeta, meta, reset } = useProjectBreadCrumbLink();

  const breadCrumbModelLinks = useMemo(() => {
    const model = [projectListLink];
    if (projectLink) {
      model.push(projectLink);
    }
    return model;
  }, [projectLink, projectListLink]);

  useEffect(() => {
    if (meta?.link) {
      const browserPath = toBrowserProjectPath(meta.link);
      if (pathname !== AppRoutes.projects.detail.url(browserPath)) {
        reset();
      }
    }
  }, [pathname, toBrowserProjectPath, meta, reset]);

  return (
    <AnchorDiv className="w-full py-1 h-full">
      <CBreadCrumbs className="my-2" home={homeLink} model={breadCrumbModelLinks} />
      <Routes>
        <Route index element={<ProjectListPage />} />
        <Route path={AppRoutes.projects.detail.path} element={<ProjectDetailPage setMeta={setMeta} />} />
        <Route path={'*'} element={<RedirectHome />} />
      </Routes>
    </AnchorDiv>
  );
};
